import React, { useState, useEffect } from 'react';
import { Bookmark } from '../../types/reader';
import { Bookmark as BookmarkIcon, Plus, Trash2, X, ArrowRight } from 'lucide-react';

export interface BookmarkDrawerProps {
  isOpen: boolean;
  currentPage: number;
  bookmarks: Bookmark[];
  isCurrentPageBookmarked: boolean;
  onClose: () => void;
  onJumpToPage: (pageNumber: number) => void;
  onAddBookmark: (note?: string) => void;
  onRemoveBookmark: (id: string) => void;
}

export const BookmarkDrawer: React.FC<BookmarkDrawerProps> = ({
  isOpen,
  currentPage,
  bookmarks,
  isCurrentPageBookmarked,
  onClose,
  onJumpToPage,
  onAddBookmark,
  onRemoveBookmark,
}) => {
  const [noteInput, setNoteInput] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAddBookmark(noteInput.trim() || undefined);
    setNoteInput('');
    setShowAddForm(false);
  };

  if (!isOpen) return null;

  return (
    <div className="drawer-overlay" onClick={onClose} role="presentation">
      <aside
        className="drawer-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Bookmarks Manager"
      >
        <div className="drawer-header">
          <div className="drawer-title-group">
            <BookmarkIcon size={18} className="drawer-icon-accent" />
            <span className="drawer-badge">TEMPORAL-ANCHORS</span>
            <h2 className="drawer-title">Bookmarks ({bookmarks.length})</h2>
          </div>
          <button className="btn-close" onClick={onClose} aria-label="Close Bookmarks">
            <X size={20} />
          </button>
        </div>

        {/* Action Button Strip */}
        <div className="drawer-subtoolbar">
          {!isCurrentPageBookmarked ? (
            showAddForm ? (
              <form onSubmit={handleAddSubmit} className="bookmark-add-form">
                <input
                  type="text"
                  placeholder="Optional log reflection (max 200 chars)..."
                  maxLength={200}
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  className="bookmark-note-input"
                  autoFocus
                />
                <div className="form-btn-row">
                  <button type="submit" className="btn-sm btn-primary">
                    Anchor Page {currentPage}
                  </button>
                  <button
                    type="button"
                    className="btn-sm btn-secondary"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                className="btn-primary-action"
                onClick={() => setShowAddForm(true)}
              >
                <Plus size={16} />
                <span>Anchor Current Page ({currentPage})</span>
              </button>
            )
          ) : (
            <div className="current-page-anchored-badge">
              <span>● Page {currentPage} is already anchored</span>
            </div>
          )}
        </div>

        {/* Bookmarks List */}
        <div className="drawer-content scrollable">
          {bookmarks.length === 0 ? (
            <div className="drawer-empty-state">
              <p>NO TEMPORAL ANCHORS RECORDED IN STASIS LOGS.</p>
              <p className="empty-state-sub">
                Anchor key pages to bookmark coordinates in the discrete continuum.
              </p>
            </div>
          ) : (
            <div className="bookmarks-list">
              {bookmarks.map((bm) => {
                const isCurrent = bm.pageNumber === currentPage;
                return (
                  <div
                    key={bm.id}
                    className={`bookmark-card ${isCurrent ? 'bookmark-current' : ''}`}
                  >
                    <div className="bookmark-card-header">
                      <span className="bookmark-page-pill">Page {bm.pageNumber}</span>
                      <span className="bookmark-date">
                        {new Date(bm.createdAt).toLocaleDateString()}
                      </span>
                      <button
                        className="bookmark-delete-btn"
                        onClick={() => onRemoveBookmark(bm.id)}
                        title="Delete bookmark"
                        aria-label={`Delete bookmark for Page ${bm.pageNumber}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    <h4 className="bookmark-title">{bm.title}</h4>
                    {bm.actTitle && (
                      <span className="bookmark-act-label">{bm.actTitle}</span>
                    )}

                    {bm.note && <p className="bookmark-note">"{bm.note}"</p>}

                    {(bm.previewSnippet || bm.textSnippet) && (
                      <p className="bookmark-snippet">
                        {bm.previewSnippet || bm.textSnippet}
                      </p>
                    )}

                    <div className="bookmark-card-footer">
                      <button
                        className="bookmark-jump-btn"
                        onClick={() => {
                          onJumpToPage(bm.pageNumber);
                          onClose();
                        }}
                      >
                        <span>Jump to Page {bm.pageNumber}</span>
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};
